import {
	ScheduleRequest,
	createSchedule,
	deleteSchedule,
	getClassSchedules,
	pauseSchedule,
	transformScheduleResponseToRule,
	transformScheduleRuleToRequest,
	unpauseSchedule,
	updateSchedule,
} from "@/api/schedules";
import { useMutation, UseMutationResult, useQuery, UseQueryResult, useQueryClient } from "@tanstack/react-query";
import { ScheduleRule } from "@/components/SchedulingModal/ScheduleTableRow";
import { formatDateAsDDMMYYYY } from "@/utils/dateTimeUtils";

export type NewScheduleRule = Omit<ScheduleRule, "id" | "modified">;
export type UpdatedScheduleRule = Omit<ScheduleRule, "modified">;

/**
 * Query to get the schedules for a specific classroom.
 *
 * @example
 * ```ts
 * const schedulesQuery = useSchedulesQuery("classroom-123", "teacher@example.com");
 * // Access the schedules
 * const schedules = schedulesQuery.data;
 * // Check loading state
 * if (schedulesQuery.isLoading) {
 *   return <LoadingSpinner />;
 * }
 * ```
 *
 * @param classroomId - The ID of the classroom to fetch schedules for
 * @param teacherUsername - The username of the teacher fetching schedules for the classroom
 * @param queryEnabled - Whether the query should be queryEnabled (defaults to true). Used to only fetch schedules when the modal is opened.
 * @returns A query result with the classroom's schedules
 */
export function useSchedulesQuery(
	classroomId: string,
	teacherUsername: string,
	queryEnabled: boolean = true
): UseQueryResult<ScheduleRule[]> {
	return useQuery({
		queryKey: ["classroomSchedules", classroomId],
		queryFn: async () => {
			const res = await getClassSchedules(teacherUsername, classroomId);
			return res.schedules.map(transformScheduleResponseToRule);
		},
		initialData: [],
		enabled: queryEnabled,
		retry: (failureCount, error) => {
			// Don't retry on authentication errors
			if (error instanceof Error && error.message.includes("401")) {
				return false;
			}
			return failureCount < 3;
		},
	});
}

export function useCreateScheduleMutation(
	classroomId: string,
	teacherUsername: string,
	options?: {
		onSuccess?: (data: ScheduleRule, variables: NewScheduleRule, context: unknown) => void;
		onError?: (error: Error, variables: NewScheduleRule, context: unknown) => void;
	}
): UseMutationResult<ScheduleRule, Error, NewScheduleRule> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (schedule: NewScheduleRule) => {
			const scheduleRequest = transformScheduleRuleToRequest(schedule);
			const res = await createSchedule(teacherUsername, classroomId, scheduleRequest);
			return transformScheduleResponseToRule(res);
		},
		onError: options?.onError,
		onSuccess: (data, variables, context) => {
			// Only update the cache on successful creation
			queryClient.setQueryData<ScheduleRule[]>(["classroomSchedules", classroomId], (old) => {
				if (!old) return [data];
				return [...old, data];
			});
			options?.onSuccess?.(data, variables, context);
		},
		onSettled: () => {
			// Always refetch after error or success to ensure we have the latest data
			queryClient.invalidateQueries({ queryKey: ["classroomSchedules", classroomId] });
		},
	});
}

export function useUpdateScheduleMutation(
	classroomId: string,
	teacherUsername: string,
	options?: {
		onSuccess?: (data: ScheduleRule, variables: UpdatedScheduleRule, context: unknown) => void;
		onError?: (error: Error, variables: UpdatedScheduleRule, context: unknown) => void;
	}
): UseMutationResult<ScheduleRule, Error, UpdatedScheduleRule> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (schedule: UpdatedScheduleRule) => {
			const scheduleRequest: ScheduleRequest = transformScheduleRuleToRequest(schedule);
			const res = await updateSchedule(teacherUsername, classroomId, schedule.id.toString(), scheduleRequest);
			return transformScheduleResponseToRule(res);
		},
		onMutate: async (updatedSchedule) => {
			// Cancel any outgoing refetches (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey: ["classroomSchedules", classroomId] });

			// Snapshot the previous value
			const previousSchedules = queryClient.getQueryData<ScheduleRule[]>(["classroomSchedules", classroomId]);

			// Optimistically update to the new value
			queryClient.setQueryData<ScheduleRule[]>(["classroomSchedules", classroomId], (old) => {
				if (!old) return old;

				return old.map((schedule) =>
					schedule.id === updatedSchedule.id
						? {
								...schedule,
								day: updatedSchedule.day,
								startTime: updatedSchedule.startTime,
								endTime: updatedSchedule.endTime,
								endDate: updatedSchedule.endDate,
								active: updatedSchedule.active,
								modified: {
									by: teacherUsername,
									date: formatDateAsDDMMYYYY(new Date()),
								},
							}
						: schedule
				);
			});

			// Return a context object with the snapshotted value
			return { previousSchedules };
		},
		onError: (err, _, context) => {
			console.error("Error updating schedule:", err);
			// If the mutation fails, use the context returned from onMutate to roll back
			if (context?.previousSchedules) {
				queryClient.setQueryData(["classroomSchedules", classroomId], context.previousSchedules);
			}
			options?.onError?.(err, _, context);
		},
		onSuccess: options?.onSuccess,
		onSettled: () => {
			// Always refetch after error or success to ensure we have the latest data
			queryClient.invalidateQueries({ queryKey: ["classroomSchedules", classroomId] });
		},
	});
}

export function useToggleScheduleActiveMutation(
	classroomId: string,
	teacherUsername: string,
	options?: {
		onSuccess?: () => void;
		onError?: (error: Error, variables: { scheduleId: number; active: boolean }, context: unknown) => void;
	}
): UseMutationResult<void, Error, { scheduleId: number; active: boolean }> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ scheduleId, active }: { scheduleId: number; active: boolean }) => {
			if (active) {
				await unpauseSchedule(teacherUsername, classroomId, scheduleId.toString());
			} else {
				await pauseSchedule(teacherUsername, classroomId, scheduleId.toString());
			}
		},
		onMutate: async ({ scheduleId, active }: { scheduleId: number; active: boolean }) => {
			await queryClient.cancelQueries({ queryKey: ["classroomSchedules", classroomId] });

			const previousSchedules = queryClient.getQueryData<ScheduleRule[]>(["classroomSchedules", classroomId]);

			// Optimistically update to the new value
			queryClient.setQueryData<ScheduleRule[]>(["classroomSchedules", classroomId], (old) => {
				if (!old) return old;
				return old.map((schedule) => (schedule.id === scheduleId ? { ...schedule, active } : schedule));
			});

			return { previousSchedules };
		},
		onError: (err: Error, _: { scheduleId: number; active: boolean }, context: { previousSchedules?: ScheduleRule[] } | undefined) => {
			console.error("Error toggling schedule:", err);
			if (context?.previousSchedules) {
				queryClient.setQueryData(["classroomSchedules", classroomId], context.previousSchedules);
			}
			options?.onError?.(err, _, context);
		},
		onSuccess: options?.onSuccess,
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ["classroomSchedules", classroomId] });
		},
	});
}

export function useDeleteScheduleMutation(
	classroomId: string,
	teacherUsername: string,
	options?: { onSuccess?: () => void; onError?: (error: Error, variables: number, context: unknown) => void }
): UseMutationResult<void, Error, number> {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (scheduleId: number) => {
			await deleteSchedule(teacherUsername, classroomId, scheduleId.toString());
		},
		onMutate: async (scheduleId: number) => {
			await queryClient.cancelQueries({ queryKey: ["classroomSchedules", classroomId] });

			const previousSchedules = queryClient.getQueryData<ScheduleRule[]>(["classroomSchedules", classroomId]);

			queryClient.setQueryData<ScheduleRule[]>(["classroomSchedules", classroomId], (old) => {
				if (!old) return old;
				return old.filter((schedule) => schedule.id !== scheduleId);
			});

			return { previousSchedules };
		},
		onError: (err: Error, vars: number, context: { previousSchedules?: ScheduleRule[] } | undefined) => {
			console.error("Error deleting schedule:", err, vars, context);
			if (context?.previousSchedules) {
				queryClient.setQueryData(["classroomSchedules", classroomId], context.previousSchedules);
			}
			options?.onError?.(err, vars, context);
		},
		onSuccess: options?.onSuccess,
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ["classroomSchedules", classroomId] });
		},
	});
}
